import { createElement } from "react";
import { CircleDollarSign, HandCoins, Percent, Users } from "lucide-react";
import type {
  OfferSection,
  OffersKpi,
  OffersStage,
  RevenueTrendPoint,
  UpgradeSegment,
} from "./types";

export const OFFERS_STAGE_OPTIONS: Array<{
  value: OffersStage;
  label: string;
}> = [
  { value: "preCheckIn", label: "Pre-Check-in" },
  { value: "dashboardCheckIn", label: "Dashboard Check-In" },
  { value: "dashboardStay", label: "Dashboard in stay" },
  { value: "dashboardCheckOut", label: "Dashboard Check-Out" },
  { value: "preCheckOut", label: "Pre-Check-Out" },
];

export const DEFAULT_OFFERS_STAGE: OffersStage = "preCheckIn";

type OffersKpiIconKey = "circleDollar" | "percent" | "users" | "handCoins";

type OffersKpiMock = {
  title: string;
  value: string;
  delta?: string;
  trend?: "positive" | "negative" | "neutral";
  iconKey: OffersKpiIconKey;
};

const KPI_ICONS = {
  circleDollar: CircleDollarSign,
  percent: Percent,
  users: Users,
  handCoins: HandCoins,
} as const;

function resolveOffersKpi(mock: OffersKpiMock): OffersKpi {
  const { title, value, delta, trend, iconKey } = mock;
  const Icon = KPI_ICONS[iconKey];
  return {
    title,
    value,
    delta,
    trend,
    icon: createElement(Icon),
  };
}

const OFFERS_KPI_MOCK_BY_STAGE: Record<OffersStage, OffersKpiMock[]> = {
  preCheckIn: [
    {
      title: "Total Revenue",
      value: "$12,560.04",
      delta: "+6.8% from last month",
      trend: "positive",
      iconKey: "circleDollar",
    },
    {
      title: "Conversion rate",
      value: "24.1%",
      delta: "+1.3% from last month",
      trend: "positive",
      iconKey: "percent",
    },
    {
      title: "Guests offered",
      value: "1,400",
      iconKey: "users",
    },
    {
      title: "Avg. Revenue",
      value: "$320",
      iconKey: "handCoins",
    },
  ],
  dashboardCheckIn: [
    {
      title: "Total Revenue",
      value: "$9,420.00",
      delta: "+4.2% from last month",
      trend: "positive",
      iconKey: "circleDollar",
    },
    {
      title: "Conversion rate",
      value: "21.9%",
      delta: "+0.7% from last month",
      trend: "positive",
      iconKey: "percent",
    },
    {
      title: "Guests offered",
      value: "1,120",
      iconKey: "users",
    },
    {
      title: "Avg. Revenue",
      value: "$286",
      iconKey: "handCoins",
    },
  ],
  dashboardStay: [
    {
      title: "Total Revenue",
      value: "$14,180.00",
      delta: "+9.5% from last month",
      trend: "positive",
      iconKey: "circleDollar",
    },
    {
      title: "Conversion rate",
      value: "29.3%",
      delta: "+2.4% from last month",
      trend: "positive",
      iconKey: "percent",
    },
    {
      title: "Guests offered",
      value: "1,890",
      iconKey: "users",
    },
    {
      title: "Avg. Revenue",
      value: "$334",
      iconKey: "handCoins",
    },
  ],
  dashboardCheckOut: [
    {
      title: "Total Revenue",
      value: "$6,880.00",
      delta: "-1.6% from last month",
      trend: "negative",
      iconKey: "circleDollar",
    },
    {
      title: "Conversion rate",
      value: "18.5%",
      delta: "-0.4% from last month",
      trend: "negative",
      iconKey: "percent",
    },
    {
      title: "Guests offered",
      value: "900",
      iconKey: "users",
    },
    {
      title: "Avg. Revenue",
      value: "$242",
      iconKey: "handCoins",
    },
  ],
  preCheckOut: [
    {
      title: "Total Revenue",
      value: "$8,060.00",
      delta: "+2.1% from last month",
      trend: "positive",
      iconKey: "circleDollar",
    },
    {
      title: "Conversion rate",
      value: "19.4%",
      delta: "+0.5% from last month",
      trend: "positive",
      iconKey: "percent",
    },
    {
      title: "Guests offered",
      value: "980",
      iconKey: "users",
    },
    {
      title: "Avg. Revenue",
      value: "$270",
      iconKey: "handCoins",
    },
  ],
};

export function getOffersKpiByStage(stage: OffersStage): OffersKpi[] {
  return OFFERS_KPI_MOCK_BY_STAGE[stage].map(resolveOffersKpi);
}

export const UPGRADE_USAGE_SEGMENTS_BY_STAGE: Record<
  OffersStage,
  UpgradeSegment[]
> = {
  preCheckIn: [
    { key: "purchased", name: "Purchased", value: 43, fill: "#CAC19F" },
    { key: "skipped", name: "Skipped", value: 45, fill: "#D4D4D4" },
    { key: "clickedOnly", name: "Clicked only", value: 12, fill: "#826BFF" },
  ],
  dashboardCheckIn: [
    { key: "purchased", name: "Purchased", value: 38, fill: "#CAC19F" },
    { key: "skipped", name: "Skipped", value: 50, fill: "#D4D4D4" },
    { key: "clickedOnly", name: "Clicked only", value: 12, fill: "#826BFF" },
  ],
  dashboardStay: [
    { key: "purchased", name: "Purchased", value: 48, fill: "#CAC19F" },
    { key: "skipped", name: "Skipped", value: 40, fill: "#D4D4D4" },
    { key: "clickedOnly", name: "Clicked only", value: 12, fill: "#826BFF" },
  ],
  dashboardCheckOut: [
    { key: "purchased", name: "Purchased", value: 34, fill: "#CAC19F" },
    { key: "skipped", name: "Skipped", value: 53, fill: "#D4D4D4" },
    { key: "clickedOnly", name: "Clicked only", value: 13, fill: "#826BFF" },
  ],
  preCheckOut: [
    { key: "purchased", name: "Purchased", value: 36, fill: "#CAC19F" },
    { key: "skipped", name: "Skipped", value: 51, fill: "#D4D4D4" },
    { key: "clickedOnly", name: "Clicked only", value: 13, fill: "#826BFF" },
  ],
};

export const REVENUE_TREND_BY_STAGE: Record<OffersStage, RevenueTrendPoint[]> =
  {
    preCheckIn: [
      { day: "Jan", revenue: 4200 },
      { day: "Feb", revenue: 6039 },
      { day: "Mar", revenue: 5100 },
      { day: "Apr", revenue: 3100 },
      { day: "May", revenue: 5600 },
      { day: "Jun", revenue: 4900 },
    ],
    dashboardCheckIn: [
      { day: "Jan", revenue: 3400 },
      { day: "Feb", revenue: 4200 },
      { day: "Mar", revenue: 3900 },
      { day: "Apr", revenue: 2800 },
      { day: "May", revenue: 4600 },
      { day: "Jun", revenue: 4300 },
    ],
    dashboardStay: [
      { day: "Jan", revenue: 5200 },
      { day: "Feb", revenue: 6400 },
      { day: "Mar", revenue: 5900 },
      { day: "Apr", revenue: 4200 },
      { day: "May", revenue: 6900 },
      { day: "Jun", revenue: 6500 },
    ],
    dashboardCheckOut: [
      { day: "Jan", revenue: 2900 },
      { day: "Feb", revenue: 3500 },
      { day: "Mar", revenue: 3300 },
      { day: "Apr", revenue: 2400 },
      { day: "May", revenue: 3900 },
      { day: "Jun", revenue: 3600 },
    ],
    preCheckOut: [
      { day: "Jan", revenue: 3000 },
      { day: "Feb", revenue: 3800 },
      { day: "Mar", revenue: 3500 },
      { day: "Apr", revenue: 2600 },
      { day: "May", revenue: 4200 },
      { day: "Jun", revenue: 4000 },
    ],
  };

export const OFFER_SECTIONS_BY_STAGE = {
  preCheckIn: [
    {
      id: "upsell",
      title: "Upsell Offers",
      metrics: [
        { title: "Total guests contacted", value: "1400" },
        { title: "Only Opened", value: "895" },
        { title: "Guests offered", value: "380" },
        { title: "Skipped", value: "120" },
      ],
      rows: [
        {
          id: "upsell-1",
          imageUrl:
            "https://images.unsplash.com/photo-1611892440504-42a792e24d32?auto=format&fit=crop&w=80&q=80",
          serviceName: "Early Check-in",
          offers: "18 offers",
          opened: "12%",
          ordered: "3%",
          skipped: "85%",
        },
        {
          id: "upsell-2",
          imageUrl:
            "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=80&q=80",
          serviceName: "Late check-out",
          offers: "74 offers",
          opened: "31%",
          ordered: "8%",
          skipped: "61%",
        },
        {
          id: "upsell-3",
          imageUrl:
            "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=80&q=80",
          serviceName: "Transfer to city",
          offers: "60 offers",
          opened: "18%",
          ordered: "4%",
          skipped: "78%",
        },
      ],
    },
    {
      id: "service",
      title: "Service Offers",
      metrics: [
        { title: "Total guests contacted", value: "3200" },
        { title: "Only Opened", value: "1895" },
        { title: "Guests offered", value: "986" },
        { title: "Skipped", value: "319" },
      ],
      rows: [
        {
          id: "service-1",
          imageUrl:
            "https://images.unsplash.com/photo-1519823551278-64ac92734fb1?auto=format&fit=crop&w=80&q=80",
          serviceName: "Breakfast in room",
          offers: "95 offers",
          opened: "36%",
          ordered: "11%",
          skipped: "53%",
        },
        {
          id: "service-2",
          imageUrl:
            "https://images.unsplash.com/photo-1584132915807-fd1f5fbc078f?auto=format&fit=crop&w=80&q=80",
          serviceName: "Pool VIP Massage",
          offers: "280 offers",
          opened: "42%",
          ordered: "15%",
          skipped: "43%",
        },
        {
          id: "service-3",
          imageUrl:
            "https://images.unsplash.com/photo-1560067174-8945c3b8f89f?auto=format&fit=crop&w=80&q=80",
          serviceName: "Roof top lounge",
          offers: "255 offers",
          opened: "38%",
          ordered: "9%",
          skipped: "53%",
        },
      ],
    },
    {
      id: "activities",
      title: "Activities offers",
      metrics: [
        { title: "Total guests contacted", value: "400" },
        { title: "Only Opened", value: "140" },
        { title: "Guests offered", value: "160" },
        { title: "Skipped", value: "100" },
      ],
      rows: [
        {
          id: "activities-1",
          imageUrl:
            "https://images.unsplash.com/photo-1530789253388-582c481c54b0?auto=format&fit=crop&w=80&q=80",
          serviceName: "Dinner at the tower",
          offers: "85 offers",
          opened: "18%",
          ordered: "6%",
          skipped: "76%",
        },
        {
          id: "activities-2",
          imageUrl:
            "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=80&q=80",
          serviceName: "Yachting tour",
          offers: "120 offers",
          opened: "27%",
          ordered: "11%",
          skipped: "62%",
        },
        {
          id: "activities-3",
          imageUrl:
            "https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=80&q=80",
          serviceName: "Lake view walk",
          offers: "92 offers",
          opened: "22%",
          ordered: "9%",
          skipped: "69%",
        },
      ],
    },
  ],
  dashboardCheckIn: [
    {
      id: "upsell",
      title: "Upsell Offers",
      metrics: [
        { title: "Total guests contacted", value: "1400" },
        { title: "Only Opened", value: "895" },
        { title: "Guests offered", value: "380" },
        { title: "Skipped", value: "120" },
      ],
      rows: [
        {
          id: "checkin-upsell-1",
          imageUrl:
            "https://images.unsplash.com/photo-1611892440504-42a792e24d32?auto=format&fit=crop&w=80&q=80",
          serviceName: "Early Check-in",
          opened: "184",
          ordered: { value: "124 orders", subValue: "76.7%" },
          skipped: { value: "40", subValue: "23.3%" },
        },
        {
          id: "checkin-upsell-2",
          imageUrl:
            "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=80&q=80",
          serviceName: "Room Upgrade",
          opened: "118",
          ordered: { value: "98 orders", subValue: "86.7%" },
          skipped: { value: "20", subValue: "13.3%" },
        },
      ],
    },
    {
      id: "service",
      title: "Service Offers",
      metrics: [
        { title: "Total guests contacted", value: "3200" },
        { title: "Only Opened", value: "1895" },
        { title: "Guests offered", value: "986" },
        { title: "Skipped", value: "319" },
      ],
      rows: [
        {
          id: "checkin-service-1",
          imageUrl:
            "https://images.unsplash.com/photo-1519823551278-64ac92734fb1?auto=format&fit=crop&w=80&q=80",
          serviceName: "Breakfast in the room",
          opened: "450",
          ordered: { value: "300 orders", subValue: "66.7%" },
          skipped: { value: "150", subValue: "33.3%" },
        },
        {
          id: "checkin-service-2",
          imageUrl:
            "https://images.unsplash.com/photo-1584132915807-fd1f5fbc078f?auto=format&fit=crop&w=80&q=80",
          serviceName: "Relaxing Massage",
          opened: "280",
          ordered: { value: "180 orders", subValue: "66.7%" },
          skipped: { value: "100", subValue: "33.3%" },
        },
        {
          id: "checkin-service-3",
          imageUrl:
            "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=80&q=80",
          serviceName: "Romantic Dinner for 2",
          opened: "218",
          ordered: { value: "175 orders", subValue: "80%" },
          skipped: { value: "43", subValue: "20%" },
        },
      ],
    },
    {
      id: "activities",
      title: "Activities offers",
      metrics: [
        { title: "Total guests contacted", value: "400" },
        { title: "Only Opened", value: "140" },
        { title: "Guests offered", value: "160" },
        { title: "Skipped", value: "100" },
      ],
      rows: [
        {
          id: "checkin-activities-1",
          imageUrl:
            "https://images.unsplash.com/photo-1511739001486-6bfe10ce785f?auto=format&fit=crop&w=80&q=80",
          serviceName: "Dinner at Eiffel Tower",
          opened: "184",
          ordered: { value: "124 orders", subValue: "76.7%" },
          skipped: { value: "40", subValue: "23.3%" },
        },
        {
          id: "checkin-activities-2",
          imageUrl:
            "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=80&q=80",
          serviceName: "Walking Tour at Montmartre",
          opened: "130",
          ordered: { value: "98 orders", subValue: "86.7%" },
          skipped: { value: "32", subValue: "13.3%" },
        },
        {
          id: "checkin-activities-3",
          imageUrl:
            "https://images.unsplash.com/photo-1566127993742-301549eadf0f?auto=format&fit=crop&w=80&q=80",
          serviceName: "Louvre",
          opened: "105",
          ordered: { value: "78 orders", subValue: "80%" },
          skipped: { value: "27", subValue: "20%" },
        },
      ],
    },
  ],
  dashboardStay: [
    {
      id: "service",
      title: "Service Offers",
      metrics: [
        { title: "Total guests contacted", value: "3200" },
        { title: "Only Opened", value: "1895" },
        { title: "Guests offered", value: "986" },
        { title: "Skipped", value: "319" },
      ],
      rows: [
        {
          id: "stay-service-1",
          imageUrl:
            "https://images.unsplash.com/photo-1519823551278-64ac92734fb1?auto=format&fit=crop&w=80&q=80",
          serviceName: "Breakfast in the room",
          opened: "450",
          ordered: { value: "300 orders", subValue: "66.7%" },
          skipped: { value: "150", subValue: "14.3%" },
        },
        {
          id: "stay-service-2",
          imageUrl:
            "https://images.unsplash.com/photo-1584132915807-fd1f5fbc078f?auto=format&fit=crop&w=80&q=80",
          serviceName: "Relaxing Massage",
          opened: "280",
          ordered: { value: "180 orders", subValue: "66.7%" },
          skipped: { value: "100", subValue: "13.3%" },
        },
        {
          id: "stay-service-3",
          imageUrl:
            "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=80&q=80",
          serviceName: "Romantic Dinner for 2",
          opened: "256",
          ordered: { value: "176 orders", subValue: "80%" },
          skipped: { value: "80", subValue: "20%" },
        },
      ],
    },
    {
      id: "activities",
      title: "Activities offers",
      metrics: [
        { title: "Total guests contacted", value: "400" },
        { title: "Only Opened", value: "140" },
        { title: "Guests offered", value: "160" },
        { title: "Skipped", value: "100" },
      ],
      rows: [
        {
          id: "stay-activities-1",
          imageUrl:
            "https://images.unsplash.com/photo-1511739001486-6bfe10ce785f?auto=format&fit=crop&w=80&q=80",
          serviceName: "Dinner at Eiffel Tower",
          opened: "164",
          ordered: { value: "124 orders", subValue: "76.7%" },
          skipped: { value: "40", subValue: "23.3%" },
        },
        {
          id: "stay-activities-2",
          imageUrl:
            "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=80&q=80",
          serviceName: "Walking Tour at Montmartre",
          opened: "130",
          ordered: { value: "98 orders", subValue: "66.7%" },
          skipped: { value: "32", subValue: "13.3%" },
        },
        {
          id: "stay-activities-3",
          imageUrl:
            "https://images.unsplash.com/photo-1566127993742-301549eadf0f?auto=format&fit=crop&w=80&q=80",
          serviceName: "Louvre",
          opened: "106",
          ordered: { value: "78 orders", subValue: "80%" },
          skipped: { value: "28", subValue: "20%" },
        },
      ],
    },
  ],
  dashboardCheckOut: [
    {
      id: "upsell",
      title: "Upsell Offers",
      metrics: [
        { title: "Total guests contacted", value: "1400" },
        { title: "Only Opened", value: "895" },
        { title: "Guests offered", value: "380" },
        { title: "Skipped", value: "120" },
      ],
      rows: [
        {
          id: "checkout-upsell-1",
          imageUrl:
            "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=80&q=80",
          serviceName: "Late Check-Out",
          opened: "172",
          ordered: { value: "112 orders", subValue: "65.1%" },
          skipped: { value: "60", subValue: "34.9%" },
        },
        {
          id: "checkout-upsell-2",
          imageUrl:
            "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=80&q=80",
          serviceName: "Transfer to Airport",
          opened: "148",
          ordered: { value: "96 orders", subValue: "64.9%" },
          skipped: { value: "52", subValue: "35.1%" },
        },
      ],
    },
    {
      id: "service",
      title: "Service Offers",
      metrics: [
        { title: "Total guests contacted", value: "3200" },
        { title: "Only Opened", value: "1895" },
        { title: "Guests offered", value: "986" },
        { title: "Skipped", value: "319" },
      ],
      rows: [
        {
          id: "checkout-service-1",
          imageUrl:
            "https://images.unsplash.com/photo-1519823551278-64ac92734fb1?auto=format&fit=crop&w=80&q=80",
          serviceName: "Breakfast in the room",
          opened: "450",
          ordered: { value: "300 orders", subValue: "66.7%" },
          skipped: { value: "150", subValue: "33.3%" },
        },
        {
          id: "checkout-service-2",
          imageUrl:
            "https://images.unsplash.com/photo-1584132915807-fd1f5fbc078f?auto=format&fit=crop&w=80&q=80",
          serviceName: "Relaxing Massage",
          opened: "280",
          ordered: { value: "180 orders", subValue: "66.7%" },
          skipped: { value: "100", subValue: "33.3%" },
        },
        {
          id: "checkout-service-3",
          imageUrl:
            "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=80&q=80",
          serviceName: "Romantic Dinner for 2",
          opened: "218",
          ordered: { value: "175 orders", subValue: "80%" },
          skipped: { value: "43", subValue: "20%" },
        },
      ],
    },
    {
      id: "activities",
      title: "Activities offers",
      metrics: [
        { title: "Total guests contacted", value: "400" },
        { title: "Only Opened", value: "140" },
        { title: "Guests offered", value: "160" },
        { title: "Skipped", value: "100" },
      ],
      rows: [
        {
          id: "checkout-activities-1",
          imageUrl:
            "https://images.unsplash.com/photo-1511739001486-6bfe10ce785f?auto=format&fit=crop&w=80&q=80",
          serviceName: "Dinner at Eiffel Tower",
          opened: "184",
          ordered: { value: "124 orders", subValue: "76.7%" },
          skipped: { value: "40", subValue: "23.3%" },
        },
        {
          id: "checkout-activities-2",
          imageUrl:
            "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=80&q=80",
          serviceName: "Walking Tour at Montmartre",
          opened: "130",
          ordered: { value: "98 orders", subValue: "86.7%" },
          skipped: { value: "32", subValue: "13.3%" },
        },
        {
          id: "checkout-activities-3",
          imageUrl:
            "https://images.unsplash.com/photo-1566127993742-301549eadf0f?auto=format&fit=crop&w=80&q=80",
          serviceName: "Louvre",
          opened: "105",
          ordered: { value: "78 orders", subValue: "80%" },
          skipped: { value: "27", subValue: "20%" },
        },
      ],
    },
  ],
  preCheckOut: [
    {
      id: "upsell-step-2",
      title: "Upsell Offers on Step 2",
      metrics: [
        { title: "Total guests contacted", value: "1640" },
        { title: "Only Opened", value: "1200" },
        { title: "Guests offered", value: "540" },
        { title: "Skipped", value: "100" },
      ],
      rows: [
        {
          id: "precheckout-step2-1",
          imageUrl:
            "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=80&q=80",
          serviceName: "Late Check-Out",
          opened: "164",
          ordered: { value: "124 orders", subValue: "76.7%" },
          skipped: { value: "40", subValue: "23.3%" },
        },
        {
          id: "precheckout-step2-2",
          imageUrl:
            "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=80&q=80",
          serviceName: "Transfer to Airport",
          opened: "118",
          ordered: { value: "98 orders", subValue: "86.7%" },
          skipped: { value: "20", subValue: "13.3%" },
        },
        {
          id: "precheckout-step2-3",
          imageUrl:
            "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=80&q=80",
          serviceName: "Transfer to Airport",
          opened: "98",
          ordered: { value: "78 orders", subValue: "80%" },
          skipped: { value: "11", subValue: "20%" },
        },
      ],
    },
    {
      id: "upsell-step-5",
      title: "Upsell Offers: Step 5",
      metrics: [
        { title: "Total guests contacted", value: "1230" },
        { title: "Only Opened", value: "230" },
        { title: "Guests offered", value: "750" },
        { title: "Skipped", value: "250" },
      ],
      rows: [
        {
          id: "precheckout-step5-1",
          imageUrl:
            "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=80&q=80",
          serviceName: "Transfer to Airport",
          opened: "118",
          ordered: { value: "98 orders", subValue: "86.7%" },
          skipped: { value: "20", subValue: "13.3%" },
        },
        {
          id: "precheckout-step5-2",
          imageUrl:
            "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=80&q=80",
          serviceName: "Transfer to Airport",
          opened: "98",
          ordered: { value: "78 orders", subValue: "80%" },
          skipped: { value: "11", subValue: "20%" },
        },
      ],
    },
  ],
} as unknown as Record<OffersStage, OfferSection[]>;

export function parseOffersStage(raw: string | null): OffersStage {
  if (!raw) {
    return DEFAULT_OFFERS_STAGE;
  }
  return OFFERS_STAGE_OPTIONS.some((item) => item.value === raw)
    ? (raw as OffersStage)
    : DEFAULT_OFFERS_STAGE;
}

export function shouldShowRoomUpgradeWidget(stage: OffersStage): boolean {
  return stage === "preCheckIn";
}
